/**
 * Network Error Detection Utility
 * 
 * Detects if an error is a network-level error (connection lost, timeout, etc.)
 * vs a server error (4xx, 5xx responses).
 */

/**
 * Check if error is a network-level error
 * Network errors occur when the request cannot reach the server at all.
 */
export function isNetworkError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return false
    }

    const err = error as Record<string, unknown>

    // TypeError: Failed to fetch (Chrome/Firefox)
    // TypeError: NetworkError when attempting to fetch resource (Firefox)
    // TypeError: Network request failed (Safari)
    if (err instanceof TypeError) {
        const message = (err as Error).message?.toLowerCase() || ''
        return message.includes('failed to fetch') ||
            message.includes('network') ||
            message.includes('load failed')
    }

    // Axios-style errors
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        return true
    }

    // No response at all (request never completed)
    if ('request' in err && !('response' in err)) {
        return true
    }

    // DOMException: The user aborted a request (can happen on navigation, but treat as transient)
    if (err.name === 'AbortError') {
        return true
    }

    return false
}

/**
 * Check if error is a 401 Unauthorized (session expired)
 */
export function isUnauthorizedError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return false
    }

    const err = error as Record<string, unknown>

    // Check response status
    const response = err.response as Record<string, unknown> | undefined
    const status = response?.status ?? err.status

    return status === 401
}
